import { observer } from 'mobx-react';
import React from 'react';
import { SingleValue } from 'react-select';
import FontBody1 from '../../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../components/fonts/font-props';
import { FormFieldLine } from '../../../../../components/form-field-line/form-field-line';
import { Input } from '../../../../../components/inputs/input/input';
import { Toggle } from '../../../../../components/inputs/toggle/toggle';
import Card from '../../../../../components/layouts/card/card';
import ControlGroup from '../../../../../components/layouts/control-group/control-group';
import { Note, NOTE_TYPE } from '../../../../../components/note/note';
import config from '../../../../../config';
import DashTranslation from '../../../../../dashboard-translation/dashboard-translaton';
import useDashboardTranslation from '../../../../../dashboard-translation/hooks/dashboard-translation-hook';
import { IValidationGroup } from '../../../../../hooks/validation-group/validation-group-hook';
import { FEATURES } from '../../../../../models/Plan';
import {
	A2A_INCENTIVE_TYPES_MAP,
	INCENTIVE_TYPES,
	INCENTIVE_VIRTUAL_CURRENCIES,
	INCENTIVE_VIRTUAL_CURRENCIES_MAP
} from '../../../../../models/User';
import currentPortalStore from '../../../../../stores/current-portal-store';
import { IOption, SelectDropdown } from '../../../../../components/inputs/select-dropdown/select-dropdown';
import FontBody2 from '../../../../../components/fonts/font-body2/font-body2';
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";


interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    validationGroup: IValidationGroup;
    dataName?: string; /** used by Candu to target an element */
}

const A2ARefundsPreventionsSettingsCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, validationGroup, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const switchToExchangeStore = currentPortalStore.setting?.switchToExchangeStore;

    const isSwitchToExchangeFeatureAllowed = currentPortalStore.isFeatureAllowed(FEATURES.SWITCH_TO_EXCHANGE);
	const switchToExchangeCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.SWITCH_TO_EXCHANGE);

    const onChangeIsSwitchToExchangeActive = (value: boolean) => {
        switchToExchangeStore?.setIsActive(value);
    }

    const incentiveVirtualCurrencyOptions = [
        {
            label: getTranslation(INCENTIVE_VIRTUAL_CURRENCIES_MAP[INCENTIVE_VIRTUAL_CURRENCIES.GIFT_CARD].label, pageScope),
            value: INCENTIVE_VIRTUAL_CURRENCIES.GIFT_CARD,
	        disabled: !switchToExchangeCapability.isGiftCardIncentiveSupported,
        },
        {
            label: getTranslation(INCENTIVE_VIRTUAL_CURRENCIES_MAP[INCENTIVE_VIRTUAL_CURRENCIES.STORE_CREDITS].label, pageScope),
            value: INCENTIVE_VIRTUAL_CURRENCIES.STORE_CREDITS,
	        disabled: !switchToExchangeCapability.isStoreCreditsIncentiveSupported,
        },
    ];

    const incentiveVirtualCurrency = switchToExchangeStore?.incentiveVirtualCurrency ? {
        value: switchToExchangeStore?.incentiveVirtualCurrency,
        label: getTranslation(INCENTIVE_VIRTUAL_CURRENCIES_MAP[switchToExchangeStore?.incentiveVirtualCurrency].label, pageScope)
    } : undefined;
    const onChangeIncentiveVirtualCurrency = (value: SingleValue<IOption>) => {
        switchToExchangeStore?.setIncentiveVirtualCurrency(value?.value as INCENTIVE_VIRTUAL_CURRENCIES);
    }

    const incentiveTypeOptions = [
        {
            label: getTranslation(A2A_INCENTIVE_TYPES_MAP[INCENTIVE_TYPES.FIXED_PRICE].label, pageScope),
            value: INCENTIVE_TYPES.FIXED_PRICE
        },
        {
            label: getTranslation(A2A_INCENTIVE_TYPES_MAP[INCENTIVE_TYPES.PERCENTAGE].label, pageScope),
            value: INCENTIVE_TYPES.PERCENTAGE
        },
    ];

    const incentiveType = switchToExchangeStore?.incentiveType ? {
        value: switchToExchangeStore?.incentiveType,
        label: getTranslation(A2A_INCENTIVE_TYPES_MAP[switchToExchangeStore?.incentiveType].label, pageScope)
    } : undefined;

    const onChangeIncentiveType = (value: SingleValue<IOption>) => {
        switchToExchangeStore?.setIncentiveType(value?.value as INCENTIVE_TYPES);
    }

    const onChangeIncentiveAmount = (value: number) => {
        switchToExchangeStore?.setIncentiveAmount(+value.toFixed(0));
    }

    return (
		<ResolveCapability capability={PROFILE_CAPABILITIES.SWITCH_TO_EXCHANGE}>
			{({ isSupported, isFeatureEnabled }) => isSupported && isFeatureEnabled && (
				<Card
					toolbar={{
						title: (
							<FontBody1 variant={FONT_VARIANT.BOLD}>
								<DashTranslation translationKey="REFUNDS_PREVENTION" scope={pageScope} />
							</FontBody1>
						)
					}}
					expandCallback={onExpandClick}
					isExpanded={isExpanded}
					dataName={dataName}
				>

					<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
						{getTranslation('SWITCH_TO_EXCHANGE', pageScope)}
					</FontBody2>

					<FormFieldLine
						tooltip={!isSwitchToExchangeFeatureAllowed ? (
							<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
								<DashTranslation translationKey="SWITCH_REFUND_REQUEST_TO_EXCHANGE_TOOLTIP" scope={pageScope} />
							</FontBody2>
						) : null
						}
						label={getTranslation('SWITCH_REFUND_REQUEST_TO_EXCHANGE', pageScope)}
						note={getTranslation('SWITCH_REFUND_REQUEST_TO_EXCHANGE_NOTE', pageScope)}
						dataName="switch-refund-request-to-exchange-a2a"
					>
						<div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
							<Toggle
								checked={!!switchToExchangeStore?.isActive}
								onChange={onChangeIsSwitchToExchangeActive}
								disabled={!isSwitchToExchangeFeatureAllowed}
								label=''
							/>
						</div>
					</FormFieldLine>

					<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
						{getTranslation('SWITCH_TO_EXCHANGE_BONUS', pageScope)}
					</FontBody2>

					{!!switchToExchangeStore?.isActive && (
						<>
							<FormFieldLine
								label={getTranslation('SELECT_INCENTIVE_AMOUNT', pageScope)}
								note={getTranslation('SELECT_INCENTIVE_AMOUNT_NOTE', pageScope)}
								dataName="select-incentive-amount-a2a"
							>
								<ControlGroup>
									<SelectDropdown
										options={incentiveTypeOptions}
										value={incentiveType}
										onChange={onChangeIncentiveType}
										required
										validationGroup={validationGroup}
									/>
									<div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
										<Input
											value={switchToExchangeStore?.incentiveAmount}
											onChange={onChangeIncentiveAmount}
											type="number"
											min={0}
											step={1}
											required
											validationGroup={validationGroup}
										/>
										<FontBody2>{switchToExchangeStore?.incentiveType === INCENTIVE_TYPES.FIXED_PRICE ? switchToExchangeStore?.incentiveCurrency : '%'}</FontBody2>
									</div>
									{!!switchToExchangeStore?.incentiveAmount && (
										<FontBody2 color={FONT_COLOR.NEUTRAL3}>
											{switchToExchangeStore?.incentiveType  === INCENTIVE_TYPES.FIXED_PRICE ? (
												<>
													{getTranslation('BONUS_AMOUNT', pageScope)} = {getTranslation('ITEMS_VALUE', pageScope)} + <FontBody2 color={FONT_COLOR.PRIMARY1}>{switchToExchangeStore?.incentiveAmount} {switchToExchangeStore?.incentiveCurrency}</FontBody2>
												</>
											) : (
												<>
													{getTranslation('BONUS_AMOUNT', pageScope)} = {getTranslation('ITEMS_VALUE', pageScope)} + <FontBody2 color={FONT_COLOR.PRIMARY1}>{switchToExchangeStore?.incentiveAmount}%</FontBody2>
												</>
											)}
										</FontBody2>
									)}
								</ControlGroup>
							</FormFieldLine>
							<FormFieldLine
								label={getTranslation('VIRTUAL_CURRENCY', pageScope)}
								note={getTranslation('VIRTUAL_CURRENCY_NOTE', pageScope)}
								dataName="virtual-currency-a2a"
							>
								<SelectDropdown
									options={incentiveVirtualCurrencyOptions}
									value={incentiveVirtualCurrency}
									onChange={onChangeIncentiveVirtualCurrency}
									placeholder='Please select'
									required
									validationGroup={validationGroup}
								/>
							</FormFieldLine>
						</>
					)}

					<Note
						type={NOTE_TYPE.INFORMATION}
						body={
							<FontBody2 color={FONT_COLOR.PRIMARY3}>
								<DashTranslation
									translationKey="SWITCH_TO_EXCHANGE_LOOK_AND_FEEL"
									params={[`${config.apiUrl}/dashboard/settings/return-portal/look-and-feel/return-portal?card_focus=general_settings`]}
									scope={pageScope}
								/>
							</FontBody2>
						}
					/>
				</Card>
			)}
		</ResolveCapability>
    );
});

export default A2ARefundsPreventionsSettingsCard;
